import React from 'react';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import FAIcon from '#/common/components/FAIcon';
import { isLightTheme } from '#/common/utils/themeDetection';

const DropZoneStyles = styled.div<{ isDragging: boolean }>`
  border: 2px dashed
    ${({ isDragging, theme }) =>
      isDragging
        ? color('ui', 'dark2')
        : isLightTheme(theme)
          ? color('ui', 'middle')
          : color('ui', 'dark')};
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  background-color: ${({ isDragging, theme }) =>
    isDragging
      ? isLightTheme(theme)
        ? color('ui', 'light2')
        : color('ui', 'dark')
      : color('custom', 'formGroupBackground')};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${color('ui', 'dark2')};
    background-color: ${({ theme }) =>
      isLightTheme(theme) ? color('ui', 'light2') : color('ui', 'dark')};
  }
`;

export interface DropZoneProps {
  displayFormats: string;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onClick: () => void;
}

const DropZone: React.FC<DropZoneProps> = ({ displayFormats, onDrop, onClick }) => {
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    onDrop(event);
  };

  return (
    <DropZoneStyles
      isDragging={isDragging}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={onClick}>
      <NuvoBox display="flex" flexDirection="column" alignItems="center" gap={16}>
        <FAIcon icon="up-from-bracket" size="2xl" color={color('primary', 'main')} />
        <NuvoText type="header3">{`Drop your ${displayFormats} file here`}</NuvoText>
        <NuvoLabel>or click to choose what to upload</NuvoLabel>
      </NuvoBox>
    </DropZoneStyles>
  );
};

export default React.memo(DropZone);
